import { useTaskTranslation } from '../hooks/useTaskTranslation';
import { hasValidCustomerId, TaskCustomerSelection } from '../hooks/useTaskCustomerSelection';
import { TaskPersonPicker } from './TaskPersonPicker';

interface TaskCustomerPickerProps {
  selection: TaskCustomerSelection;
  disabled?: boolean;
  clearLabel?: string;
  onSelectionChange?: () => void;
}

export const TaskCustomerPicker = ({
  selection,
  disabled = false,
  clearLabel,
  onSelectionChange,
}: TaskCustomerPickerProps) => {
  const { translations } = useTaskTranslation();
  return (
    <TaskPersonPicker
      label={translations('task_board.customer')}
      searchLabel={translations('task_board.search_customers')}
      searchPlaceholder={translations('task_board.search_by_name_email_phone_or_customer_id')}
      listLabel={translations('task_board.choose_a_customer')}
      search={selection.search}
      onSearchChange={selection.setSearch}
      options={
        selection.customersReady
          ? selection.filteredOptions.map(({ customer, key }) => ({
              key,
              name: [customer.person_firstname, customer.person_lastname].filter(Boolean).join(' '),
              details: [customer.person_email, customer.person_mobile_phone]
                .filter(Boolean)
                .join(' · '),
              disabled: !hasValidCustomerId(customer),
            }))
          : []
      }
      selectedKey={selection.selectedKey}
      selectedName={
        selection.selectedCustomer &&
        [selection.selectedCustomer.person_firstname, selection.selectedCustomer.person_lastname]
          .filter(Boolean)
          .join(' ')
      }
      onSelect={(key) => {
        selection.selectCustomer(key);
        onSelectionChange?.();
      }}
      onClear={() => {
        selection.clearSelection();
        onSelectionChange?.();
      }}
      clearLabel={clearLabel ?? translations('task_board.clear_selection')}
      disabled={disabled}
      loading={selection.customersPending}
      error={
        selection.customersError ? translations('task_board.unable_to_load_customers') : undefined
      }
      onRetry={selection.retryCustomers}
      emptyMessage={
        selection.hasCustomers
          ? translations('task_board.no_customers_match_your_search')
          : translations('task_board.no_customers_available')
      }
    >
      {selection.detailsPending && (
        <div role="status">{translations('task_board.loading_customer_details')}</div>
      )}
      {selection.detailsError && (
        <div role="alert">
          {selection.detailsError}{' '}
          <button type="button" onClick={selection.retryDetails}>
            {translations('task_board.retry_customer_details')}
          </button>
        </div>
      )}
    </TaskPersonPicker>
  );
};
